import { useAuth0 } from '@auth0/auth0-react';

import { useIsFeatureOn } from 'hooks/useFeatureSwitch/useIsFeatureOn';
import useProjectRoute from 'hooks/useProjectRoute';
import useSampleProject from './useSampleProjects/useSampleProject';

type IfExact<T, U, Y, N> = [T] extends [U] ? ([U] extends [T] ? Y : N) : N;

export type UseStreamSwitchOptions<T, R> = {
  streamResource: T;
  apiResource: R;
} & IfExact<
  T,
  R,
  { transform?: (data: R) => T },
  { transform: (data: R) => T }
>;

export function useStreamEnabled(options?: { projectId?: string }): boolean {
  const { features } = useIsFeatureOn();
  const { accountId, projectId: projectIdFromRoute } = useProjectRoute();
  const projectId = options?.projectId || projectIdFromRoute;
  const { user } = useAuth0();
  const { inSampleProject } = useSampleProject();

  if (inSampleProject) {
    return false;
  }

  const feature = features?.find(f => f.name === 'event_sourcing');
  if (feature?.enabled) {
    return true;
  }

  // this is intended to always enable event sourcing for developers testing in dev namespace
  if (user?.email && feature?.whitelist?.users?.includes(user.email)) {
    return true;
  }

  // otherwise look for a feature flag with the project or account id in the name
  const foundFeature = features?.find(
    f =>
      (f.name === `event_sourcing/${projectId}` ||
        f.name === `event_sourcing/${accountId}`) &&
      f.enabled,
  );

  return !!foundFeature;
}

export function useStreamSwitch<T, R>({
  transform,
  streamResource,
  apiResource,
}: UseStreamSwitchOptions<T, R>): T {
  const streamEnabled = useStreamEnabled();

  if (streamEnabled) {
    return streamResource;
  }

  if (transform) {
    return transform(apiResource);
  }

  return apiResource as unknown as T;
}

export function useRestApiEnabled(): boolean {
  const { isLoadingFeatures } = useIsFeatureOn();
  const isStreamEnabled = useStreamEnabled();

  return !isLoadingFeatures && !isStreamEnabled;
}
